import type { SpaceLimit } from '../../../hooks/useSpaceLimits';

import { use, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import BuyMoreButton from '../../../components/BuyMoreButton/BuyMoreButton';
import LinkButton from '../../../components/LinkButton/LinkButton';

import useSelectedSpace from '../../../hooks/useSelectedSpace';
import { useSpaceLimits } from '../../../hooks/useSpaceLimits';

import UserContext from '../../../contexts/UserContext';

import { CaretDownIcon, CaretRightIcon } from '../../../images/shapes';

const USAGE_FIELDS = [
  'ctd_limit',
  'cto_limit',
  'team_members_limit',
  'file_quota',
  'scoped_keys_limit',
  'api_calls_limit',
  'official_plugins_limit',
  'webhooks_limit',
];

const findColor = (resources: SpaceLimit) => {
  if (resources.percent >= 95) {
    return { bar: 'bg-red-600', background: 'bg-red-50 dark:bg-red-950' };
  } else if (resources.percent >= 80) {
    return {
      bar: 'bg-orange',
      background: 'bg-yellow-50 dark:bg-orange-950',
    };
  } else {
    return { bar: 'bg-blue', background: 'bg-white dark:bg-slate-950' };
  }
};

const DashboardStats = ({ testId = '' }: { testId?: string }) => {
  const { t } = useTranslation();
  const { spaceLimits, buildUrlWithSpace, space } = useSelectedSpace();
  const { data: resources } = useSpaceLimits(spaceLimits, USAGE_FIELDS);
  const { isAdmin } = use(UserContext);

  const [isAll, toggleAll] = useReducer((state) => !state, false);

  const resourceLinks = {
    ctd_limit: {
      label: t('Home.AddNew'),
      link: buildUrlWithSpace('content-type-definitions/add'),
      hide: false,
    },
    team_members_limit: {
      label: t('Global.Invite'),
      link: '/users/add',
      hide: !isAdmin,
    },
    official_plugins_limit: {
      label: t('Global.SeeMore'),
      link: buildUrlWithSpace('plugins'),
      hide: false,
    },
    webhooks_limit: {
      label: t('Home.AddNew'),
      link: buildUrlWithSpace('/webhooks/add'),
      hide: false,
    },
  };

  return (
    <section {...getTestProps(testId, 'stats')}>
      <h2 className="sr-only">{t('Spaces.SpaceLimits')}</h2>

      <ul
        id="space-usage"
        className="grid auto-rows-fr grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5 lg:gap-6"
      >
        {resources.map((resource, index) => {
          const color = findColor(resource);
          const resourceLink =
            resourceLinks[resource.key as keyof typeof resourceLinks];

          return (
            <li
              key={resource.key}
              className={twMerge(
                'relative h-full rounded-lg p-3 pb-4 pr-6 border border-gray-200 dark:border-slate-800 dark:text-white',
                color.background,
                index >= 4 && !isAll ? 'hidden sm:block' : '',
              )}
              {...getTestProps(testId, resource.key)}
            >
              <strong
                className="block text-4xl my-1.5 font-bold leading-none"
                {...getTestProps(testId, `${resource.key}-value`)}
              >
                {resource.value ?? 0}
              </strong>

              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-lg">
                <span {...getTestProps(testId, `${resource.key}-limit`)}>
                  / {resource.limitLabel}
                </span>
                <span {...getTestProps(testId, `${resource.key}-label`)}>
                  {resource.label}
                </span>
              </p>

              {!resource.isUnlimited && (
                <div
                  className={twMerge(
                    'mt-7 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700',
                  )}
                >
                  <div
                    className={twMerge('h-full rounded-full', color.bar)}
                    style={{ width: `${resource.percent}%` }}
                    {...getTestProps(testId, `${resource.key}-progress-bar`)}
                  />
                </div>
              )}

              {resourceLink && !resourceLink.hide && resource.percent < 100 && (
                <LinkButton
                  link={resourceLink.link}
                  buttonColor="borderless"
                  additionalClasses="absolute p-2 top-1 right-4"
                  iconImage={<CaretRightIcon className="size-3" />}
                  {...getTestProps(
                    testId,
                    `${resource.key}-add-link`,
                    'testId',
                  )}
                >
                  {resourceLink.label}
                </LinkButton>
              )}

              {resource.percent >= 100 && (
                <BuyMoreButton
                  detail={resource}
                  spaceId={space!}
                  planLimits={spaceLimits!}
                  additionalClasses="absolute p-2 top-1 right-4"
                  {...getTestProps(testId, 'stats', 'testId')}
                />
              )}
            </li>
          );
        })}
      </ul>

      <Button
        buttonColor="borderlessBlue"
        iconImage={
          <CaretDownIcon
            className={twMerge('size-3', isAll ? 'rotate-180' : '')}
          />
        }
        onClick={toggleAll}
        aria-expanded={isAll}
        aria-controls="space-usage"
        additionalClasses="sm:hidden py-2 mt-4 mx-auto"
        {...getTestProps(testId, 'show-more', 'testId')}
      >
        {isAll ? t('Global.Collapse') : t('Global.SeeMore')}
      </Button>
    </section>
  );
};

export default DashboardStats;
